import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const DashboardThemeContext = createContext(null);
const STORAGE_KEY = "dashboardTheme";

const defaultTheme = {
  theme_mode: "light",
  primary_color: "#2563EB",
  button_color: "#2563EB",
  button_text_color: "#FFFFFF",
  table_header_color: "#1E40AF",
  table_header_text_color: "#FFFFFF",
  sidebar_color: "#111827",
  sidebar_text_color: "#FFFFFF",
  use_default_colors: true,
};

const lightSystemColors = {
  dashboard_background: "#F3F4F6",
  card_background: "#FFFFFF",
  text_color: "#111827",
  muted_text_color: "#6B7280",
  border_color: "#E5E7EB",
  table_background: "#FFFFFF",
  input_background: "#FFFFFF",
};

const darkSystemColors = {
  dashboard_background: "#020617",
  card_background: "#0F172A",
  text_color: "#F9FAFB",
  muted_text_color: "#94A3B8",
  border_color: "#1E293B",
  table_background: "#0B1220",
  input_background: "#111827",
};

const normalizeTheme = (data = {}) => ({
  ...defaultTheme,
  theme_mode: data.theme_mode || defaultTheme.theme_mode,
  primary_color: data.primary_color || defaultTheme.primary_color,
  button_color: data.button_color || defaultTheme.button_color,
  button_text_color: data.button_text_color || defaultTheme.button_text_color,
  table_header_color: data.table_header_color || defaultTheme.table_header_color,
  table_header_text_color: data.table_header_text_color || defaultTheme.table_header_text_color,
  sidebar_color: data.sidebar_color || defaultTheme.sidebar_color,
  sidebar_text_color: data.sidebar_text_color || defaultTheme.sidebar_text_color,
  use_default_colors:
    typeof data.use_default_colors === "boolean"
      ? data.use_default_colors
      : defaultTheme.use_default_colors,
});

const getInitialTheme = () => {
  try {
    const savedTheme = localStorage.getItem(STORAGE_KEY);

    if (!savedTheme) {
      return defaultTheme;
    }

    return normalizeTheme(JSON.parse(savedTheme));
  } catch {
    return defaultTheme;
  }
};

const saveTheme = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeTheme(theme)));
  } catch {}
};

const removeTheme = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const buildTableStyles = (theme) => ({
  wrapper: {
    borderRadius: { xs: "10px", md: "12px" },
    border: `1px solid ${theme.border_color}`,
    bgcolor: theme.card_background,
    overflow: "hidden",
  },
  toolbar: {
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderBottom: `1px solid ${theme.border_color}`,
    bgcolor: theme.card_background,
  },
  tableArea: {
    p: { xs: 1, sm: 1.2 },
    bgcolor: theme.card_background,
  },
  container: {
    width: "100%",
    overflowX: "auto",
    bgcolor: theme.table_background,
    borderRadius: "12px",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  headRow: {
    bgcolor: theme.table_header_color,
    "& th:first-of-type": {
      borderTopLeftRadius: "10px",
    },
    "& th:last-of-type": {
      borderTopRightRadius: "10px",
    },
  },
  headCell: {
    color: theme.table_header_text_color,
    fontWeight: 700,
    py: 1.4,
    whiteSpace: "nowrap",
    borderBottom: "none",
    bgcolor: theme.table_header_color,
  },
  bodyRow: {
    bgcolor: theme.table_background,
    "& td": {
      borderBottom: `1px solid ${theme.border_color}`,
      color: theme.text_color,
      py: 1.35,
    },
    "&:last-child td": {
      borderBottom: "none",
    },
  },
  emptyCell: {
    py: 5,
    color: theme.muted_text_color,
    borderBottom: "none",
    bgcolor: theme.table_background,
  },
  pagination: {
    borderTop: `1px solid ${theme.border_color}`,
    bgcolor: theme.card_background,
    color: theme.text_color,
    "& .MuiTablePagination-toolbar": {
      minHeight: 52,
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      color: theme.muted_text_color,
      fontSize: 13,
    },
    "& .MuiIconButton-root": {
      color: theme.text_color,
    },
    "& .Mui-disabled": {
      color: theme.muted_text_color,
      opacity: 0.45,
    },
  },
});

const buildInputStyles = (theme) => ({
  "& .MuiInputBase-root": {
    borderRadius: "12px",
    bgcolor: theme.input_background,
    color: theme.text_color,
  },
  "& .MuiInputBase-input": {
    color: theme.text_color,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& .MuiInputBase-input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
    borderRadius: "12px",
    transition: "background-color 9999s ease-in-out 0s",
  },
  "& .MuiInputBase-input:-webkit-autofill:hover": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& .MuiInputBase-input:-webkit-autofill:focus": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& .MuiInputBase-input:-webkit-autofill:active": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
    borderRadius: "12px",
    transition: "background-color 9999s ease-in-out 0s",
  },
  "& input:-webkit-autofill:hover": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& input:-webkit-autofill:focus": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& input:-webkit-autofill:active": {
    WebkitBoxShadow: `0 0 0 1000px ${theme.input_background} inset`,
    WebkitTextFillColor: theme.text_color,
    caretColor: theme.text_color,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: theme.text_color,
    WebkitTextFillColor: theme.text_color,
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    color: theme.muted_text_color,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.primary_color,
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: theme.muted_text_color,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.border_color,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.primary_color,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.primary_color,
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.border_color,
  },
});

const buildButtonStyles = (theme) => ({
  contained: {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    bgcolor: theme.button_color,
    color: theme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: theme.button_color,
      opacity: 0.92,
      boxShadow: "none",
    },
  },
});

const buildTheme = (theme) => {
  const systemColors = theme.theme_mode === "dark" ? darkSystemColors : lightSystemColors;

  const mergedTheme = {
    ...theme,
    ...systemColors,
  };

  return {
    ...mergedTheme,
    tableStyles: buildTableStyles(mergedTheme),
    inputStyles: buildInputStyles(mergedTheme),
    buttonStyles: buildButtonStyles(mergedTheme),
  };
};

export const DashboardThemeProvider = ({ children }) => {
  const { accessToken, isAuthenticated, authChecking, logout } = useAuth();
  const [storedTheme, setStoredTheme] = useState(getInitialTheme);
  const [themeLoading, setThemeLoading] = useState(false);

  const dashboardTheme = useMemo(() => buildTheme(storedTheme), [storedTheme]);

  const applyTheme = (theme) => {
    const normalized = normalizeTheme(theme);
    saveTheme(normalized);
    setStoredTheme(normalized);
    return normalized;
  };

  const loadDashboardTheme = async () => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) {
      removeTheme();
      setStoredTheme(defaultTheme);
      return;
    }

    try {
      setThemeLoading(true);

      const response = await fetch("/api/preferences/dashboard-theme/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 401) {
        removeTheme();
        logout("Error de conexión. Inicia sesión nuevamente.");
        return;
      }

      if (response.ok) {
        applyTheme(data);
      }
    } catch {
      const cachedTheme = getInitialTheme();
      setStoredTheme(cachedTheme);
    } finally {
      setThemeLoading(false);
    }
  };

  const updateDashboardTheme = async (payload) => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) return { ok: false };

    const response = await fetch("/api/preferences/dashboard-theme/update/", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (response.status === 401) {
      removeTheme();
      logout("Error de conexión. Inicia sesión nuevamente.");
      return { ok: false, data };
    }

    if (response.ok) {
      applyTheme(data);
      return { ok: true, data };
    }

    return { ok: false, data };
  };

  const toggleThemeMode = async () => {
    const nextMode = storedTheme.theme_mode === "dark" ? "light" : "dark";

    const nextTheme = applyTheme({
      ...storedTheme,
      theme_mode: nextMode,
    });

    const result = await updateDashboardTheme({
      theme_mode: nextMode,
    });

    if (!result.ok) {
      applyTheme(storedTheme);
    }

    return {
      ...result,
      theme: nextTheme,
    };
  };

  const resetDashboardTheme = async () => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) return { ok: false };

    const response = await fetch("/api/preferences/dashboard-theme/reset/", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (response.status === 401) {
      removeTheme();
      logout("Error de conexión. Inicia sesión nuevamente.");
      return { ok: false, data };
    }

    if (response.ok) {
      applyTheme(data);
      return { ok: true, data };
    }

    return { ok: false, data };
  };

  useEffect(() => {
    if (authChecking) return;

    if (isAuthenticated) {
      loadDashboardTheme();
      return;
    }

    removeTheme();
    setStoredTheme(defaultTheme);
  }, [authChecking, isAuthenticated, accessToken]);

  const value = useMemo(
    () => ({
      dashboardTheme,
      storedTheme,
      themeLoading,
      loadDashboardTheme,
      updateDashboardTheme,
      resetDashboardTheme,
      toggleThemeMode,
    }),
    [dashboardTheme, storedTheme, themeLoading]
  );

  return (
    <DashboardThemeContext.Provider value={value}>
      {children}
    </DashboardThemeContext.Provider>
  );
};

export const useDashboardTheme = () => useContext(DashboardThemeContext);